import axios from 'axios';

// In development: proxy forwards /api/* to localhost:5000 (see package.json "proxy")
// In production:  REACT_APP_API_URL may be either the App Service root or /api path.
const normalizeApiBase = (value) => {
  const trimmed = (value || '/api').replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

const API = normalizeApiBase(process.env.REACT_APP_API_URL);

const imageTypeByExtension = {
  jpg:  'image/jpeg',
  jpeg: 'image/jpeg',
  png:  'image/png',
  gif:  'image/gif',
  webp: 'image/webp',
  svg:  'image/svg+xml',
};

const getImageContentType = (file) => {
  if (file?.type) return file.type;
  const ext = (file?.name || '').split('.').pop()?.toLowerCase();
  return imageTypeByExtension[ext] || 'application/octet-stream';
};

const resolveApiUrl = (url) => {
  if (!url || /^https?:\/\//i.test(url)) return url || '';
  if (url.startsWith('/api') && API !== '/api') {
    return `${API.replace(/\/api\/?$/, '')}${url}`;
  }
  return url;
};

// ── Attach Bearer token to every request ──────────────────────────────────────
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('gs_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── On 401, clear session and force re-login ──────────────────────────────────
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url?.includes('/auth/login')) {
      sessionStorage.removeItem('gs_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:          (school_code, username, password) =>
    axios.post(`${API}/auth/login`, { school_code, username, password }),
  changePassword: (current_password, new_password) =>
    axios.post(`${API}/auth/change-password`, { current_password, new_password }),
  verifyPassword: (current_password) =>
    axios.post(`${API}/auth/verify-password`, { current_password }),
  updateProfile:  (data) =>
    axios.patch(`${API}/auth/school-profile`, data),
  getFeatureFlags: () =>
    axios.get(`${API}/auth/feature-flags`),
};

// ── Admin (founder-only) ───────────────────────────────────────────────────
export const adminAPI = {
  listSchools:    ()                => axios.get(`${API}/admin/schools`),
  createSchool:   (data)            => axios.post(`${API}/admin/schools`, data),
  deleteSchool:   (code)            => axios.delete(`${API}/admin/schools/${code}`),
  resetPassword:  (code, pass)      => axios.post(`${API}/admin/schools/${code}/reset-password`, { new_password: pass }),
  listSchoolUsers:   (code)                    => axios.get(`${API}/admin/schools/${code}/users`),
  resetSchoolUserPassword: (code, userId, pass) =>
    axios.post(`${API}/admin/schools/${code}/users/${userId}/reset-password`, { new_password: pass }),
  toggleActive:        (code) => axios.patch(`${API}/admin/schools/${code}/toggle-active`),
  toggleWhatsApp:      (code) => axios.patch(`${API}/admin/schools/${code}/toggle-whatsapp`),
  toggleCertificates:  (code) => axios.patch(`${API}/admin/schools/${code}/toggle-certificates`),
  toggleBiometric:            (code) => axios.patch(`${API}/admin/schools/${code}/toggle-biometric`),
  toggleEmployeeBiometric:    (code) => axios.patch(`${API}/admin/schools/${code}/toggle-employee-biometric`),
  toggleStudentBiometric:     (code) => axios.patch(`${API}/admin/schools/${code}/toggle-student-biometric`),
  toggleSMS:                  (code) => axios.patch(`${API}/admin/schools/${code}/toggle-sms`),
  editSchool:     (code, data)      => axios.patch(`${API}/admin/schools/${code}`, data),
  getStats:       (code)            => axios.get(`${API}/admin/schools/${code}/stats`),
  getSmsLog:      ()                => axios.get(`${API}/admin/schools/sms-log`),
};

// ── Public school registration ────────────────────────────────────────────────
export const schoolsAPI = {
  checkCode: (code) => axios.get(`${API}/admin/schools/check/${encodeURIComponent(code)}`),
  register:  (data) => axios.post(`${API}/admin/schools/register`, data),
};

export const schoolLogoAPI = {
  getCurrent: () => axios.get(`${API}/school-logo`),
  upload:    (file) => axios.put(`${API}/school-logo`, file, {
    headers: { 'Content-Type': getImageContentType(file) },
  }),
  publicUrl: (schoolCode, version = '') =>
    `${API}/school-logo/public/${encodeURIComponent(schoolCode)}${version ? `?v=${encodeURIComponent(version)}` : ''}`,
  resolveUrl: resolveApiUrl,
};

// ── School User Management (Access Control) ───────────────────────────────────
export const usersAPI = {
  getAll:  ()          => axios.get(`${API}/users`),
  create:  (data)      => axios.post(`${API}/users`, data),
  update:  (id, data)  => axios.put(`${API}/users/${id}`, data),
  delete:  (id)        => axios.delete(`${API}/users/${id}`),
};

// ── Internal Chat ─────────────────────────────────────────────────────────────
export const chatAPI = {
  listThreads: ()                     => axios.get(`${API}/chat/threads`),
  getMessages: (thread_key, since_id = 0) =>
    axios.get(`${API}/chat/messages`, { params: { thread_key, since_id } }),
  send:        (thread_key, content, thread_title) =>
    axios.post(`${API}/chat/messages`, { thread_key, content, thread_title }),
  sendWhatsapp:(thread_key, content, thread_title) =>
    axios.post(`${API}/chat/messages/whatsapp`, { thread_key, content, thread_title }),
  markRead:    (thread_key, message_id) =>
    axios.post(`${API}/chat/read`, { thread_key, message_id }),
  getReadStatus:(thread_key) =>
    axios.get(`${API}/chat/read-status`, { params: { thread_key } }),
};

// ── Meeting Room ─────────────────────────────────────────────────────────────
export const meetingAPI = {
  heartbeat:            (is_muted)                      => axios.post(`${API}/meeting/heartbeat`, { is_muted }),
  leave:                ()                              => axios.delete(`${API}/meeting/leave`),
  presence:             ()                              => axios.get(`${API}/meeting/presence`),
  signal:               (to_user, signal_type, payload) => axios.post(`${API}/meeting/signal`, { to_user, signal_type, payload }),
  signals:              (since_id = 0)                  => axios.get(`${API}/meeting/signals`, { params: { since_id } }),
  getSchedules:         ()                              => axios.get(`${API}/meeting/schedules`),
  createSchedule:       (data)                          => axios.post(`${API}/meeting/schedules`, data),
  deleteSchedule:       (id)                            => axios.delete(`${API}/meeting/schedules/${id}`),
  getStaff:             ()                              => axios.get(`${API}/meeting/staff`),
  getNotifications:     ()                              => axios.get(`${API}/meeting/notifications`),
  markNotificationRead: (id)                            => axios.patch(`${API}/meeting/notifications/${id}/read`),
  callInvite:           (to_user)                       => axios.post(`${API}/meeting/call-invite`, { to_user }),
};

// ── Students ──────────────────────────────────────────────────────────────────
export const studentsAPI = {
  getAll:      (params) => axios.get(`${API}/students`, { params }),
  getById:     (id)     => axios.get(`${API}/students/${id}`),
  create:      (data)   => axios.post(`${API}/students`, data),
  update:      (id, data) => axios.put(`${API}/students/${id}`, data),
  delete:      (id)     => axios.delete(`${API}/students/${id}`),
  getVillages: ()       => axios.get(`${API}/students/meta/villages`),
  getClasses:  (year)   => axios.get(`${API}/students/meta/classes`, { params: year ? { year } : {} }),
  checkDuplicate: (student_name, father_name, exclude_id) =>
    axios.get(`${API}/students/meta/check-duplicate`, { params: { student_name, father_name, exclude_id } }),
  // Archive / TC
  setStatus:   (id, status, tc_date) => axios.patch(`${API}/students/${id}/status`, { status, tc_date }),
  // Promote all students to next class + year
  promote:           (data)   => axios.post(`${API}/students/promote`, data),
  promoteIndividual: (data)   => axios.post(`${API}/students/promote-individual`, data),
  // Bulk fee update for a class
  bulkFee:     (data)   => axios.post(`${API}/students/bulk-fee`, data),
  // Bulk import from CSV
  importCSV:   (data)   => axios.post(`${API}/students/import-csv`, data),
};

// ── Payments ──────────────────────────────────────────────────────────────────
export const paymentsAPI = {
  getByStudent:    (studentId) => axios.get(`${API}/payments/student/${studentId}`),
  create:          (data)      => axios.post(`${API}/payments`, data),
  delete:          (id)        => axios.delete(`${API}/payments/${id}`),
  deleteByReceipt: (receiptNo) => axios.delete(`${API}/payments/by-receipt`, { params: { rno: receiptNo } }),
  getCustomSummary: (year)     => axios.get(`${API}/payments/custom-summary`, { params: { year } }),
};

// ── Installments ──────────────────────────────────────────────────────────────
export const installmentsAPI = {
  getByStudent:   (studentId, year) => axios.get(`${API}/installments/student/${studentId}`, { params: { year } }),
  getOverdueIds:  (year)            => axios.get(`${API}/installments/overdue-ids`, { params: { year } }),
  create:       (data)            => axios.post(`${API}/installments`, data),
  update:       (id, data)        => axios.put(`${API}/installments/${id}`, data),
  delete:       (id)              => axios.delete(`${API}/installments/${id}`),
};

// ── Reports ───────────────────────────────────────────────────────────────────
export const reportsAPI = {
  getDashboard:       (year) => axios.get(`${API}/reports/dashboard`,        { params: { year } }),
  getVillageReport:   (year) => axios.get(`${API}/reports/village`,          { params: { year } }),
  getClassReport:     (year) => axios.get(`${API}/reports/class`,            { params: { year } }),
  getPendingStudents: (year) => axios.get(`${API}/reports/pending-students`, { params: { year } }),
  getAllStudents:      (year) => axios.get(`${API}/reports/all-students`,     { params: { year } }),
  getMonthlyChart:    (year) => axios.get(`${API}/reports/monthly-chart`,    { params: { year } }),
  getOverdueCount:    (year) => axios.get(`${API}/reports/overdue-count`,    { params: { year } }),
  getCollectionBook:  (params) => axios.get(`${API}/reports/collection-book`, { params }),
  getDayBook:         (params) => axios.get(`${API}/reports/collection-book`, { params: { ...params, group_by: 'detail' } }),
  getStrength:          (year) => axios.get(`${API}/reports/strength`,           { params: { year } }),
  getArchivedStudents:  ()     => axios.get(`${API}/reports/archived-students`),
  getMonthlyPayments:   (year) => axios.get(`${API}/reports/monthly-payments`,   { params: { year } }),
};

// ── Fee Types (admin-managed custom fee categories, scoped per academic year) ──
export const feeTypesAPI = {
  getAll:  (year)                          => axios.get(`${API}/fee-types`, { params: { year } }),
  create:  (fee_name, fee_amount, year)    => axios.post(`${API}/fee-types`, { fee_name, fee_amount, year }),
  update:  (id, fee_name, fee_amount)      => axios.put(`${API}/fee-types/${id}`, { fee_name, fee_amount }),
  delete:  (id)                            => axios.delete(`${API}/fee-types/${id}`),
};


// ── Fee Templates (default fees per class per academic year) ─────────────────
export const feeTemplatesAPI = {
  getAll:          (year)            => axios.get(`${API}/fee-templates`, { params: { year } }),
  upsert:          (cls, year, data) => axios.put(`${API}/fee-templates/${encodeURIComponent(year)}/${encodeURIComponent(cls)}`, data),
  delete:          (cls, year)       => axios.delete(`${API}/fee-templates/${encodeURIComponent(year)}/${encodeURIComponent(cls)}`),
  verifyPassword:  (password)        => axios.post(`${API}/fee-templates/verify-password`, { password }),
  changePassword:  (current_password, new_password) =>
    axios.put(`${API}/fee-templates/password`, { current_password, new_password }),
  forgotPassword:  (login_password, new_password) =>
    axios.post(`${API}/fee-templates/forgot-password`, { login_password, new_password }),
};

// ── Hostel Fee Templates (per-school, per-year rows with name + amount) ───────
export const hostelFeeTemplatesAPI = {
  getAll: (year)                                 => axios.get(`${API}/hostel-fee-templates`, { params: { year } }),
  create: (year, fee_name, fee_term1, fee_term2) => axios.post(`${API}/hostel-fee-templates`, { year, fee_name, fee_term1, fee_term2 }),
  update: (id, fee_name, fee_term1, fee_term2)   => axios.put(`${API}/hostel-fee-templates/${id}`, { fee_name, fee_term1, fee_term2 }),
  delete: (id)                                   => axios.delete(`${API}/hostel-fee-templates/${id}`),
};

// ── Transport Fee Templates (per-school, per-year rows with village + amount) ─
export const transportFeeTemplatesAPI = {
  getAll: (year)                                    => axios.get(`${API}/transport-fee-templates`, { params: { year } }),
  create: (year, village_name, fee_term1, fee_term2) => axios.post(`${API}/transport-fee-templates`, { year, village_name, fee_term1, fee_term2 }),
  update: (id, village_name, fee_term1, fee_term2)   => axios.put(`${API}/transport-fee-templates/${id}`, { village_name, fee_term1, fee_term2 }),
  delete: (id)                                       => axios.delete(`${API}/transport-fee-templates/${id}`),
};

// ── Text-to-Speech─ Text-to-Speech ────────────────────────────────────────────────────────────
export const ttsAPI = {
  // Returns audio/mpeg ArrayBuffer
  speak: (text) => axios.post(`${API}/tts/speak`, { text }, { responseType: 'arraybuffer' }),
};

// ── Exams & Results ───────────────────────────────────────────────────────────
export const examsAPI = {
  getAll:        (params)       => axios.get(`${API}/exams`, { params }),
  getById:       (id)           => axios.get(`${API}/exams/${id}`),
  create:        (data)         => axios.post(`${API}/exams`, data),
  update:        (id, data)     => axios.put(`${API}/exams/${id}`, data),
  delete:        (id)           => axios.delete(`${API}/exams/${id}`),
  getResults:    (id)           => axios.get(`${API}/exams/${id}/results`),
  saveResults:   (id, results)  => axios.post(`${API}/exams/${id}/results`, { results }),
  clearResults:  (id)           => axios.delete(`${API}/exams/${id}/results`),
};

export const homeworkAPI = {
  getAll:  (params) => axios.get(`${API}/homework`, { params }),
  create:  (data)   => axios.post(`${API}/homework`, data),
  delete:  (id)     => axios.delete(`${API}/homework/${id}`),
};

export const plannerAPI = {
  getTimetable:    (params) => axios.get(`${API}/planner/timetable`, { params }),
  createTimetable: (data)   => axios.post(`${API}/planner/timetable`, data),
  deleteTimetable: (id)     => axios.delete(`${API}/planner/timetable/${id}`),
  getEvents:       (params) => axios.get(`${API}/planner/events`, { params }),
  createEvent:     (data)   => axios.post(`${API}/planner/events`, data),
  deleteEvent:     (id)     => axios.delete(`${API}/planner/events/${id}`),
};

export const examScheduleAPI = {
  getAll:  (params) => axios.get(`${API}/planner/exam-schedule`, { params }),
  create:  (data)   => axios.post(`${API}/planner/exam-schedule`, data),
  delete:  (id)     => axios.delete(`${API}/planner/exam-schedule/${id}`),
};

// ── Expenditures ──────────────────────────────────────────────────────────────
export const expendituresAPI = {
  getAll:  (params) => axios.get(`${API}/expenditures`, { params }),
  create:  (data)   => axios.post(`${API}/expenditures`, data),
  update:  (id, data) => axios.put(`${API}/expenditures/${id}`, data),
  delete:  (id)     => axios.delete(`${API}/expenditures/${id}`),
};

// ── Investment Expenditures ───────────────────────────────────────────────────
export const investmentExpendituresAPI = {
  getAll:  (params) => axios.get(`${API}/investment-expenditures`, { params }),
  create:  (data)   => axios.post(`${API}/investment-expenditures`, data),
  update:  (id, data) => axios.put(`${API}/investment-expenditures/${id}`, data),
  delete:  (id)     => axios.delete(`${API}/investment-expenditures/${id}`),
};

// ── Teacher Salaries ──────────────────────────────────────────────────────────
export const teacherSalariesAPI = {
  getAll:  (params) => axios.get(`${API}/teacher-salaries`, { params }),
  create:  (data)   => axios.post(`${API}/teacher-salaries`, data),
  delete:  (id)     => axios.delete(`${API}/teacher-salaries/${id}`),
};

// ── Employees ─────────────────────────────────────────────────────────────────
export const employeesAPI = {
  getAll:  ()          => axios.get(`${API}/employees`),
  create:  (data)      => axios.post(`${API}/employees`, data),
  update:  (id, data)  => axios.put(`${API}/employees/${id}`, data),
  delete:  (id)        => axios.delete(`${API}/employees/${id}`),
};

// ── Attendance ────────────────────────────────────────────────────────────────
export const attendanceAPI = {
  get:        (params) => axios.get(`${API}/attendance`, { params }),
  save:       (data)   => axios.post(`${API}/attendance`, data),
  getSummary: (params) => axios.get(`${API}/attendance/summary`, { params }),
  getDates:   (params) => axios.get(`${API}/attendance/dates`, { params }),
};

// ── Employee Attendance ──────────────────────────────────────────────────────
export const employeeAttendanceAPI = {
  get:        (params) => axios.get(`${API}/employee-attendance`, { params }),
  save:       (data)   => axios.post(`${API}/employee-attendance`, data),
  getSummary: (params) => axios.get(`${API}/employee-attendance/summary`, { params }),
  getDates:   (params) => axios.get(`${API}/employee-attendance/dates`, { params }),
};

// ── Salary (auto-calculated from attendance) ─────────────────────────────────
export const salaryAPI = {
  getConfig: ()           => axios.get(`${API}/salary/config`),
  setConfig: (data)       => axios.put(`${API}/salary/config`, data),
  getMonthly: (month)     => axios.get(`${API}/salary/monthly`, { params: { month } }),
  getSlips:  (month)      => axios.get(`${API}/salary/slips`,   { params: { month } }),
  freeze:    (month)      => axios.post(`${API}/salary/freeze`, { month }),
  unfreeze:  (month)      => axios.delete(`${API}/salary/slips`, { params: { month } }),
};

// ── Transport (buses, routes, stops, student assignment) ────────────────────
export const transportAPI = {
  // Buses
  listBuses:   ()             => axios.get(`${API}/transport/buses`),
  createBus:   (data)         => axios.post(`${API}/transport/buses`, data),
  updateBus:   (id, data)     => axios.put(`${API}/transport/buses/${id}`, data),
  deleteBus:   (id)           => axios.delete(`${API}/transport/buses/${id}`),
  // Routes
  listRoutes:  ()             => axios.get(`${API}/transport/routes`),
  createRoute: (data)         => axios.post(`${API}/transport/routes`, data),
  updateRoute: (id, data)     => axios.put(`${API}/transport/routes/${id}`, data),
  deleteRoute: (id)           => axios.delete(`${API}/transport/routes/${id}`),
  // Stops
  listStops:   (routeId)      => axios.get(`${API}/transport/routes/${routeId}/stops`),
  createStop:  (routeId, d)   => axios.post(`${API}/transport/routes/${routeId}/stops`, d),
  bulkAddStops:(routeId, names) => axios.post(`${API}/transport/routes/${routeId}/stops/bulk`, { names }),
  updateStop:  (stopId, d)    => axios.put(`${API}/transport/stops/${stopId}`, d),
  deleteStop:  (stopId)       => axios.delete(`${API}/transport/stops/${stopId}`),
  // Villages already defined in Operations → Transport Fee Template
  listVillages: (year)        => axios.get(`${API}/transport/villages`, { params: { year } }),
  // Assignment
  listAssignments: (params)   => axios.get(`${API}/transport/assignments`, { params }),
  assignStudent:   (studentId, data) => axios.put(`${API}/transport/assign/${studentId}`, data),
  clearAssignment: (studentId) => axios.delete(`${API}/transport/assign/${studentId}`),
  // Printable sheet
  getRouteSheet: (routeId)    => axios.get(`${API}/transport/route-sheet/${routeId}`),
  // Drivers (with PIN)
  listDrivers:  ()            => axios.get(`${API}/transport/drivers`),
  createDriver: (data)        => axios.post(`${API}/transport/drivers`, data),
  updateDriver: (id, data)    => axios.put(`${API}/transport/drivers/${id}`, data),
  deleteDriver: (id)          => axios.delete(`${API}/transport/drivers/${id}`),
};

// ── Driver + Bus Trip Tracking ───────────────────────────────────────────────
// The driver token lives in localStorage.gs_driver_token so it doesn't clash
// with sessionStorage.gs_token used by school staff. A separate axios instance
// injects it for /transport-trip/* calls.
const driverAxios = axios.create();
driverAxios.interceptors.request.use((config) => {
  const t = localStorage.getItem('gs_driver_token');
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const driverAPI = {
  // Public: PIN login
  login: (data) => axios.post(`${API}/transport-trip/driver-login`, data),
  saveSession: ({ token, driver_id, driver_name, bus_id, bus_number, school_code }) => {
    localStorage.setItem('gs_driver_token', token);
    localStorage.setItem('gs_driver_info', JSON.stringify({ driver_id, driver_name, bus_id, bus_number, school_code }));
  },
  getSession: () => {
    const t = localStorage.getItem('gs_driver_token');
    const info = localStorage.getItem('gs_driver_info');
    if (!t) return null;
    try { return { token: t, ...JSON.parse(info || '{}') }; } catch { return { token: t }; }
  },
  logout: () => {
    localStorage.removeItem('gs_driver_token');
    localStorage.removeItem('gs_driver_info');
  },
  me: () => driverAxios.get(`${API}/transport-trip/me`),
};

// Trip API works with EITHER driver JWT (via driverAxios) OR staff JWT (via
// default axios). Call withStaff=true to use the staff session.
export const busTripAPI = {
  today:       ({ route_id, trip_type, staff } = {}) => {
    const client = staff ? axios : driverAxios;
    return client.get(`${API}/transport-trip/today`, { params: { route_id, trip_type } });
  },
  event:       (tripId, data, { staff } = {}) => {
    const client = staff ? axios : driverAxios;
    return client.post(`${API}/transport-trip/trip/${tripId}/event`, data);
  },
  end:         (tripId, { staff } = {}) => {
    const client = staff ? axios : driverAxios;
    return client.post(`${API}/transport-trip/trip/${tripId}/end`);
  },
  getTrip:     (tripId, { staff } = {}) => {
    const client = staff ? axios : driverAxios;
    return client.get(`${API}/transport-trip/trip/${tripId}`);
  },
};

// ── WhatsApp Bulk Messaging ──────────────────────────────────────────────────────────
export const whatsappAPI = {
  preview:      (params) => axios.get(`${API}/whatsapp/preview`, { params }),
  sendTemplate: (data)   => axios.post(`${API}/whatsapp/send-template`, data),
  sendBulk:     (data)   => axios.post(`${API}/whatsapp/send-bulk`, data),
};

// ── School Settings (key-value) ───────────────────────────────────────────────
export const settingsAPI = {
  getAll: ()           => axios.get(`${API}/settings`),
  set:    (key, value) => axios.put(`${API}/settings/${key}`, { value }),
};

// ── Custom Message Templates (per-school) ─────────────────────────────────────
// Used by WhatsApp Bulk and the Messages composer to share a single template
// library across the app. Built-in templates live in src/utils/messageTemplates.js.
export const messageTemplatesAPI = {
  getAll: ()           => axios.get(`${API}/message-templates`),
  create: (data)       => axios.post(`${API}/message-templates`, data),
  update: (id, data)   => axios.put(`${API}/message-templates/${id}`, data),
  delete: (id)         => axios.delete(`${API}/message-templates/${id}`),
};

export const pushAPI = {
  getVapidKey:   ()     => axios.get(`${API}/push/vapid-public-key`),
  subscribe:     (sub)  => axios.post(`${API}/push/subscribe`, sub),
  unsubscribe:   (sub)  => axios.post(`${API}/push/unsubscribe`, sub),
};

// ── Biometric Verification ────────────────────────────────────────────────────
export const biometricAPI = {
  // Staff registry
  getStaff:       ()       => axios.get(`${API}/biometric/staff`),
  addStaff:       (data)   => axios.post(`${API}/biometric/staff`, data),
  updateStaff:    (id, d)  => axios.put(`${API}/biometric/staff/${id}`, d),
  deleteStaff:    (id)     => axios.delete(`${API}/biometric/staff/${id}`),
  // Staff attendance log
  getAttendance:  (params) => axios.get(`${API}/biometric/attendance`, { params }),
  punchAttendance:(data)   => axios.post(`${API}/biometric/attendance`, data),
  deleteLog:      (id)     => axios.delete(`${API}/biometric/attendance/${id}`),
  // Summary
  getSummary:     (params) => axios.get(`${API}/biometric/summary`, { params }),
  // Student biometric
  getStudentClasses:        (params) => axios.get(`${API}/biometric/student-classes`, { params }),
  getStudentsByClass:       (params) => axios.get(`${API}/biometric/students`, { params }),
  getStudentAttendance:     (params) => axios.get(`${API}/biometric/student-attendance`, { params }),
  punchStudentAttendance:   (data)   => axios.post(`${API}/biometric/student-attendance`, data),
  deleteStudentLog:         (id)     => axios.delete(`${API}/biometric/student-attendance/${id}`),
  getStudentSummary:        (params) => axios.get(`${API}/biometric/student-summary`, { params }),
};
